import { createApi } from '@reduxjs/toolkit/query/react';

import { authQuery } from './baseQuery';
import type { SUPPORTED_TABLES_ENUM } from 'src/bop-view-definitions';

export type IBopViewDefinition = {
  id: string;
  tableName: SUPPORTED_TABLES_ENUM;
  columns: string[];
  createdAt?: string;
  updatedAt?: string;
};

export type ICreateBopViewDefinition = {
  tableName: SUPPORTED_TABLES_ENUM;
  columns: string[];
};

export type IUpdateBopViewDefinition = Partial<ICreateBopViewDefinition>;

export const bopViewDefinitionsAPISlice = createApi({
  reducerPath: 'presentation-service-bop-view-definitions-api',
  tagTypes: ['BopViewDefinitions'],
  baseQuery: (args: any, api: any, extraOptions: any) =>
    authQuery(
      args,
      api,
      (extraOptions = { ...extraOptions, baseUrl: window.RUNTIME_CONFIG.PRESENTATION_SERVICE })
    ),
  endpoints: (builder) => ({
    getBopViewDefinitions: builder.query<
      IBopViewDefinition[],
      { tableName?: SUPPORTED_TABLES_ENUM } | void
    >({
      query: (params) => {
        const tableName = params?.tableName;

        return `/bop-view-definitions${tableName ? `?tableName=${tableName}` : ''}`;
      },
      providesTags: ['BopViewDefinitions'],
    }),

    getBopViewDefinitionById: builder.query<IBopViewDefinition, { id: string }>({
      query: ({ id }) => `/bop-view-definitions/${id}`,
      providesTags: ['BopViewDefinitions'],
    }),

    createBopViewDefinition: builder.mutation<IBopViewDefinition, ICreateBopViewDefinition>({
      query: (requestData) => ({
        url: `/bop-view-definitions`,
        method: 'POST',
        body: requestData,
      }),
      invalidatesTags: ['BopViewDefinitions'],
    }),

    updateBopViewDefinition: builder.mutation<
      IBopViewDefinition,
      { id: string; requestData: IUpdateBopViewDefinition }
    >({
      query: ({ id, requestData }) => ({
        url: `/bop-view-definitions/${id}`,
        method: 'PATCH',
        body: requestData,
      }),
      invalidatesTags: ['BopViewDefinitions'],
    }),

    deleteBopViewDefinition: builder.mutation<IBopViewDefinition, { id: string }>({
      query: ({ id }) => ({
        url: `/bop-view-definitions/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['BopViewDefinitions'],
    }),
  }),
});

export const {
  useGetBopViewDefinitionsQuery,
  useGetBopViewDefinitionByIdQuery,
  useCreateBopViewDefinitionMutation,
  useUpdateBopViewDefinitionMutation,
  useDeleteBopViewDefinitionMutation,
} = bopViewDefinitionsAPISlice;
